import { useState, useEffect } from 'react';
import { User, LogOut, Settings, ShoppingCart, Heart, LayoutDashboard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '@/lib/auth/authService';
import AuthModal from './auth/AuthModal';

interface UserMenuProps {
  className?: string;
}

export default function UserMenu({ className = '' }: UserMenuProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    // Comprobar si el usuario está autenticado al cargar el componente
    const authenticated = authService.isAuthenticated();
    setIsAuthenticated(authenticated);

    if (authenticated) {
      const user = authService.getCurrentUser();
      setUserData(user);
    }
  }, []);

  const handleLogout = () => {
    // Implementar la lógica de cierre de sesión
    localStorage.removeItem('supabase.auth.token');
    localStorage.removeItem('supabase.auth.user');
    setIsAuthenticated(false);
    setUserData(null);
    setIsMenuOpen(false);
    window.location.reload(); // Recargar la página para aplicar los cambios
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className={`relative ${className}`}>
      {isAuthenticated ? (
        // Usuario autenticado - mostrar avatar/botón de perfil
        <button
          className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          onClick={toggleMenu}
          aria-label="Menú de usuario"
        >
          {userData?.user_metadata?.name ? (
            <span className="text-white font-medium text-sm">
              {userData.user_metadata.name.charAt(0).toUpperCase()}
            </span>
          ) : (
            <User className="w-5 h-5 text-white" />
          )}
        </button>
      ) : (
        // Usuario no autenticado - mostrar botón de login
        <button
          onClick={() => setIsAuthModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#FCAA0D]/50 text-white transition-all duration-300"
        >
          <User className="w-4 h-4" />
          <span>Acceder</span>
        </button>
      )}

      {/* Menú desplegable para usuarios autenticados */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-64 rounded-xl overflow-hidden bg-zinc-900 border border-white/10 shadow-xl z-50"
          >
            <div className="p-4 border-b border-white/10">
              <div className="font-medium text-white">
                {userData?.user_metadata?.name || 'Usuario'}
              </div>
              <div className="text-sm text-zinc-400 truncate">
                {userData?.email}
              </div>
            </div>
            <div className="py-2">
              <a
                href="/mi-perfil"
                className="flex items-center gap-3 px-4 py-2 text-white hover:bg-white/5 transition-colors"
              >
                <User className="w-4 h-4 text-[#FCAA0D]" />
                <span>Mi Perfil</span>
              </a>
              <a
                href="/mi-dashboard"
                className="flex items-center gap-3 px-4 py-2 text-white hover:bg-white/5 transition-colors"
              >
                <LayoutDashboard className="w-4 h-4 text-[#FCAA0D]" />
                <span>Mi Dashboard</span>
              </a>
              <a
                href="/mis-favoritos"
                className="flex items-center gap-3 px-4 py-2 text-white hover:bg-white/5 transition-colors"
              >
                <Heart className="w-4 h-4 text-[#FCAA0D]" />
                <span>Mis Favoritos</span>
              </a>
              <a
                href="/mis-compras"
                className="flex items-center gap-3 px-4 py-2 text-white hover:bg-white/5 transition-colors"
              >
                <ShoppingCart className="w-4 h-4 text-[#FCAA0D]" />
                <span>Mis Compras</span>
              </a>
              <a
                href="/ajustes"
                className="flex items-center gap-3 px-4 py-2 text-white hover:bg-white/5 transition-colors"
              >
                <Settings className="w-4 h-4 text-[#FCAA0D]" />
                <span>Ajustes</span>
              </a>
            </div>
            <div className="py-2 border-t border-white/10">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-2 w-full text-left text-white hover:bg-white/5 transition-colors"
              >
                <LogOut className="w-4 h-4 text-red-400" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de autenticación */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccessfulAuth={() => {
          setIsAuthenticated(true);
          const user = authService.getCurrentUser();
          setUserData(user);
        }}
      />
    </div>
  );
}
